// Community SEO landing pages — one unique, verified entry per Dubai community.
// Content is drawn from the approved community-SEO brief. No invented ratings,
// counts or certifications. Handover context is advisory and dated.

export const COMMUNITY_HUB = "/property-snagging-dubai-communities";

// Human labels for related links that point at service pages / the hub.
export const RELATED_LABELS = {
  "/property-snagging-dubai": "Property snagging Dubai",
  "/handover-inspection-dubai": "Handover inspection Dubai",
  "/dlp-warranty-inspection-dubai": "DLP / warranty inspection",
  "/property-snagging-dubai-communities": "All Dubai communities",
};

// Friendly label for the handover status chip.
export const HANDOVER_STATUS_LABEL = {
  "developer-scheduled": "Handover scheduled by developer",
  "near-completion": "Near completion / handover underway",
  "recent-handover-dlp": "Recently handed over — DLP window",
  "ongoing": "Ongoing / phased handovers",
  "research-only": "Handover date being reverified",
};

export const COMMUNITY_PAGES = {
  "property-snagging-al-furjan": {
    communityName: "Al Furjan",
    developerName: "Azizi Developments",
    locationLabel: "Al Furjan",
    emirate: "Dubai",
    projects: ["Azizi Central"],
    propertyTypes: ["Apartments"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "developer-scheduled",
    handoverSummary:
      "Handover for Azizi Central was announced to commence in July 2026. Owners and investors can prepare for acceptance by arranging an independent snagging inspection ahead of the developer walkthrough.",
    uniqueSalesAngle:
      "We specialise in apartment handover inspections in Al Furjan — including upcoming phases such as Azizi Central — with remote-owner reporting and streamlined access coordination for a smooth acceptance.",
    inspectionFocus: [
      "Joinery, cabinetry and internal doors with hardware",
      "Wall, ceiling and floor finishes",
      "Balconies and accessible external areas",
      "Visible plumbing and electrical fixtures",
      "Air-conditioning operation and airflow",
    ],
    faqs: [
      { q: "When is handover expected for Azizi Central in Al Furjan?", a: "Handover for Azizi Central was announced to commence in July 2026. We recommend verifying the exact date for your unit with Azizi Developments." },
      { q: "Can Fedari inspect my apartment if I am an overseas owner?", a: "Yes. Once lawful and safe access is arranged we can inspect on your behalf and deliver the report digitally, so overseas owners can manage acceptance remotely." },
      { q: "What is the handover process in Al Furjan?", a: "It typically involves a developer walkthrough followed by handover documentation. We recommend an independent snagging inspection before you sign final acceptance." },
      { q: "How does Fedari help with access coordination in Al Furjan?", a: "We coordinate with you and, where needed, your appointed representative or the developer so the inspection can proceed smoothly at the scheduled time." },
      { q: "What should I focus on during an Al Furjan handover inspection?", a: "Key areas for apartments include joinery, finishes, balconies, plumbing, electrical fixtures, doors and wall/floor coverings — all documented in our report." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/dlp-warranty-inspection-dubai", "/property-snagging-dubai-communities"],
    title: "Al Furjan Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging in Al Furjan for upcoming handovers like Azizi Central. Report delivery within the confirmed timeframe. Get an exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Al Furjan",
  },

  "property-snagging-sobha-hartland": {
    communityName: "Sobha Hartland",
    developerName: "Sobha Realty",
    locationLabel: "Sobha Hartland",
    emirate: "Dubai",
    projects: ["Crest Grande"],
    propertyTypes: ["Apartments", "Waterfront towers"],
    inspectionIntent: "Handover & DLP snagging",
    handoverStatus: "near-completion",
    handoverSummary:
      "Crest Grande has received its Building Completion Certificate with customer handovers announced to commence, and the broader Sobha Hartland pipeline continues through 2026 — a key area for both handover and DLP inspections.",
    uniqueSalesAngle:
      "We provide thorough handover and post-handover DLP inspections for premium waterfront apartments in Sobha Hartland, with photo-led documentation that supports acceptance and your defect-liability follow-up.",
    inspectionFocus: [
      "Premium finishes and joinery",
      "Waterproofing, balconies and wet areas",
      "MEP systems and fixtures",
      "Sanitary ware and tiling",
      "Paintwork and surface quality",
    ],
    faqs: [
      { q: "When can I expect handover for Crest Grande in Sobha Hartland?", a: "Handovers for Crest Grande have been announced to commence following receipt of its Building Completion Certificate. Please confirm exact dates with Sobha Realty." },
      { q: "What is a DLP inspection in Sobha Hartland?", a: "A Defect Liability Period (DLP) inspection is carried out after handover to document visible and accessible findings within the applicable period, so they can be submitted to the responsible party for follow-up. Coverage and rectification remain subject to the applicable contract." },
      { q: "Does Fedari offer DLP inspections for Sobha Hartland properties?", a: "Yes. We document findings during the post-handover warranty period, subject to the terms of your contract and the developer's process." },
      { q: "What is the typical scope of a handover inspection for Sobha Hartland apartments?", a: "We inspect visible and accessible elements including finishes, fixtures, MEP systems, balconies and paintwork so quality is verified before you accept." },
      { q: "How does Fedari's report assist with claims in Sobha Hartland?", a: "Our detailed, photo-led reports document each finding with a severity label, giving you clear evidence to submit to the responsible party for follow-up during the applicable period." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/dlp-warranty-inspection-dubai", "/property-snagging-sobha-one", "/property-snagging-sobha-reserve", "/property-snagging-sobha-hartland-2", "/property-snagging-dubai-communities"],
    title: "Sobha Hartland Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent snagging and DLP inspections in Sobha Hartland. Report delivery within the confirmed timeframe for Crest Grande and other phases. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Sobha Hartland",
  },

  "property-snagging-central-park-city-walk": {
    communityName: "Central Park at City Walk",
    developerName: "Meraas",
    locationLabel: "City Walk",
    emirate: "Dubai",
    projects: ["Castleton", "Laurel", "Fern", "Thyme", "Erin"],
    propertyTypes: ["Apartments"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "near-completion",
    handoverSummary:
      "Multiple phases of Central Park at City Walk — including Castleton, Laurel, Fern, Thyme and Erin — were in final works and handover preparation as of mid-2026, an ongoing handover period for premium apartments.",
    uniqueSalesAngle:
      "We focus on detailed inspections of finishes, balconies and accessible systems for the high-specification apartments at Central Park at City Walk, so quality is verified before acceptance.",
    inspectionFocus: [
      "Premium finishes and joinery",
      "Balcony integrity and safety",
      "Accessible MEP systems",
      "Window and door functionality",
      "Aesthetic and surface quality",
    ],
    faqs: [
      { q: "When is handover expected for Central Park at City Walk?", a: "As of mid-2026, several phases were in final preparation for handover. Specific phase completion dates should be confirmed with Meraas." },
      { q: "What aspects are checked in a Central Park apartment inspection?", a: "We focus on premium finishes, balcony integrity, window and door functionality, accessible MEP systems and overall aesthetic quality." },
      { q: "Is an independent inspection necessary for new apartments in City Walk?", a: "Yes. Even for premium properties, an independent inspection identifies minor defects and snags that may be overlooked, helping ensure the quality you expect." },
      { q: "How does Fedari assist with inspection access for Central Park apartments?", a: "We work with you and the developer to schedule the inspection at a suitable time with safe, lawful access to the property." },
      { q: "What makes Fedari's inspection valuable for Central Park residents?", a: "Our inspectors deliver a detailed, photo-led report, giving you clear evidence for any developer follow-up. The reporting timeframe is confirmed in the accepted quotation; an eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-madinat-jumeirah-living", "/property-snagging-dubai-communities"],
    title: "Central Park City Walk Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging in Central Park at City Walk for premium apartments. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Central Park at City Walk",
  },

  "property-snagging-madinat-jumeirah-living": {
    communityName: "Madinat Jumeirah Living",
    developerName: "Meraas",
    locationLabel: "Madinat Jumeirah Living",
    emirate: "Dubai",
    projects: ["Lamaa"],
    propertyTypes: ["Apartments"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "near-completion",
    handoverSummary:
      "The Lamaa phase of Madinat Jumeirah Living was reported in its final stages and preparing for handover as of mid-2026 — an active handover period for premium apartments within this master community.",
    uniqueSalesAngle:
      "For premium apartment owners at Madinat Jumeirah Living, we provide expert handover inspections focused on high-standard finishes and construction, with digital reporting ideal for remote owners.",
    inspectionFocus: [
      "Premium interior finishes",
      "Terraces and balcony areas",
      "MEP system connections",
      "Sanitary ware and tiling",
      "Aesthetic detailing",
    ],
    faqs: [
      { q: "When is handover for Lamaa at Madinat Jumeirah Living?", a: "As of mid-2026, the Lamaa phase was in final stages of preparation for handover. Please check with Meraas for the exact schedule." },
      { q: "How can overseas owners arrange an inspection in Madinat Jumeirah Living?", a: "We assist overseas owners regularly — once lawful access is arranged we conduct the inspection and provide a full digital report." },
      { q: "What areas are covered in an MJL apartment inspection?", a: "We cover all visible and accessible areas including premium finishes, joinery, MEP systems, balconies and sanitary ware." },
      { q: "Is a snagging inspection important for properties in Madinat Jumeirah Living?", a: "Yes. For premium developments, an independent inspection helps ensure the finishes and construction meet expectations before you fully accept the property." },
      { q: "What is the report delivery time for Madinat Jumeirah Living inspections?", a: "You receive a detailed, photo-led report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-central-park-city-walk", "/property-snagging-dubai-communities"],
    title: "Madinat Jumeirah Living Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging in Madinat Jumeirah Living for premium apartments. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Madinat Jumeirah Living",
  },

  "property-snagging-sobha-hartland-2": {
    communityName: "Sobha Hartland II",
    developerName: "Sobha Realty",
    locationLabel: "Sobha Hartland II",
    emirate: "Dubai",
    projects: [],
    propertyTypes: ["Apartments", "Villas"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "developer-scheduled",
    handoverSummary:
      "Sobha Hartland II is part of Sobha Realty's extensive 2026 Dubai handover pipeline, with various villa and apartment phases scheduled. Independent inspections are valuable for these upcoming deliveries.",
    uniqueSalesAngle:
      "We provide expert handover inspections for both villas and apartments in Sobha Hartland II — specialised scope for larger villa areas and meticulous checks for high-rise living.",
    inspectionFocus: [
      "Villa exteriors and accessible roofing",
      "Apartment finishes and fixtures",
      "MEP systems",
      "Joinery and cabinetry",
      "Overall build quality",
    ],
    faqs: [
      { q: "When is handover expected for Sobha Hartland II?", a: "Sobha Hartland II is included in Sobha Realty's 2026 Dubai handover pipeline. Specific phase dates should be verified directly with the developer." },
      { q: "What is the inspection scope for villas in Sobha Hartland II?", a: "For villas we cover larger areas including safe, accessible external elements such as roofing, facades and grounds, plus interior finishes and MEP systems." },
      { q: "How does Fedari ensure quality for Sobha Hartland II apartments?", a: "We carry out thorough checks on finishes, fixtures, MEP systems and joinery, and deliver a detailed report. The reporting timeframe is confirmed in the accepted quotation; an eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing." },
      { q: "Can I arrange an inspection for a villa I'm buying off-plan in Sobha Hartland II?", a: "Yes. As soon as lawful access is granted and the property is ready, we can conduct a comprehensive assessment." },
      { q: "What's the benefit of a pre-handover inspection for Sobha Hartland II?", a: "It identifies snags and visible findings early, giving you clear documentation to submit to the responsible party for follow-up before you formally accept." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-sobha-hartland", "/property-snagging-sobha-one", "/property-snagging-sobha-reserve", "/property-snagging-dubai-communities"],
    title: "Sobha Hartland II Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging for villas and apartments in Sobha Hartland II. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Sobha Hartland II",
  },

  "property-snagging-sobha-one": {
    communityName: "Sobha One",
    developerName: "Sobha Realty",
    locationLabel: "Sobha One",
    emirate: "Dubai",
    projects: [],
    propertyTypes: ["Apartments"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "developer-scheduled",
    handoverSummary:
      "Sobha One is a prominent development named in Sobha's 2026 Dubai handover pipeline, with preparations for this high-rise apartment project underway.",
    uniqueSalesAngle:
      "We offer specialised handover inspections for high-rise apartments at Sobha One, with detailed checks of all accessible elements and prompt digital reporting for a smooth, informed acceptance.",
    inspectionFocus: [
      "Apartment interior finishes",
      "MEP systems and fixtures",
      "Balcony and terrace areas",
      "Joinery and cabinetry",
      "Internal door and window installations",
    ],
    faqs: [
      { q: "When is handover for Sobha One expected?", a: "Sobha One is part of Sobha's 2026 handover pipeline. For precise dates, please refer to official communications from Sobha Realty." },
      { q: "What are the key areas inspected in Sobha One apartments?", a: "We cover all visible and accessible areas including finishes, MEP systems, balconies, joinery and internal installations." },
      { q: "Can Fedari inspect my Sobha One apartment if I'm abroad?", a: "Yes. If lawful access is arranged we can inspect on your behalf and deliver the detailed report digitally, wherever you are." },
      { q: "How does report delivery work for Sobha One inspections?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing, in which case you receive your comprehensive, photo-led report within eight hours of the completed inspection." },
      { q: "Why is an independent inspection important for Sobha One?", a: "It provides a neutral assessment of your property's condition and identifies snags that may need attention before you formally accept the unit." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-sobha-hartland", "/property-snagging-sobha-hartland-2", "/property-snagging-sobha-reserve", "/property-snagging-dubai-communities"],
    title: "Sobha One Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging for Sobha One apartments. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Sobha One",
  },

  "property-snagging-sobha-reserve": {
    communityName: "Sobha Reserve",
    developerName: "Sobha Realty",
    locationLabel: "Sobha Reserve",
    emirate: "Dubai",
    projects: [],
    propertyTypes: ["Villas"],
    inspectionIntent: "Villa handover snagging",
    handoverStatus: "developer-scheduled",
    handoverSummary:
      "Sobha Reserve is listed in Sobha's 2026 Dubai handover pipeline, indicating upcoming villa deliveries. Inspections for larger villa plots focus on external elements and overall build quality.",
    uniqueSalesAngle:
      "We provide specialised handover inspections for the larger villas at Sobha Reserve, focusing on safe, accessible external areas such as roofing and facades alongside meticulous interior checks.",
    inspectionFocus: [
      "Villa roofing and external finishes",
      "MEP systems",
      "Grounds and landscaping (where accessible)",
      "Joinery and cabinetry",
      "Visual structural condition",
    ],
    faqs: [
      { q: "When can I expect handover for my villa in Sobha Reserve?", a: "Sobha Reserve is part of the 2026 handover schedule. Please confirm precise dates with Sobha Realty for your specific villa." },
      { q: "What is the inspection scope for villas in Sobha Reserve?", a: "Our villa inspections cover safe, accessible external elements like roofing, facades and grounds, alongside detailed checks of interior finishes, MEP systems and joinery." },
      { q: "Can Fedari inspect villa roofs at Sobha Reserve?", a: "We inspect accessible roofing and external areas where it is safe and practical, focusing on visible elements as part of a comprehensive handover assessment." },
      { q: "How is a villa inspection report structured?", a: "Reports are photo-led, organised by area and finding, with clear severity labels and recommended actions, delivered within the confirmed timeframe." },
      { q: "What if I am overseas and buying a villa in Sobha Reserve?", a: "We regularly inspect for overseas buyers. Once lawful access is arranged we perform the inspection and provide a full digital report." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-sobha-hartland", "/property-snagging-sobha-hartland-2", "/property-snagging-sobha-one", "/property-snagging-dubai-communities"],
    title: "Sobha Reserve Villa Snagging | Fedari Inspection",
    metaDescription:
      "Independent villa snagging for Sobha Reserve — roofing, exteriors and interiors documented in a report delivered within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Sobha Reserve",
  },

  "property-snagging-jumeirah-lake-towers": {
    communityName: "Jumeirah Lake Towers (JLT)",
    developerName: "Sobha Realty (Verde)",
    locationLabel: "Jumeirah Lake Towers (JLT)",
    emirate: "Dubai",
    projects: ["Verde by Sobha"],
    propertyTypes: ["Apartments"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "developer-scheduled",
    handoverSummary:
      "Verde by Sobha is named in the 2026 handover pipeline for JLT — an upcoming period for apartment handovers in this prominent Dubai district.",
    uniqueSalesAngle:
      "We offer precise handover inspections for apartments in Jumeirah Lake Towers, including projects such as Verde by Sobha, verifying finishes, MEP systems and overall build for a confident acceptance.",
    inspectionFocus: [
      "Apartment finishes and fixtures",
      "MEP systems",
      "Balcony and window integrity",
      "Joinery and fittings",
      "Paintwork and wall finishes",
    ],
    faqs: [
      { q: "When is handover expected for Verde by Sobha in JLT?", a: "Verde by Sobha is listed in the 2026 handover pipeline. For specific dates for your unit, please consult Sobha Realty." },
      { q: "What is covered in a JLT apartment handover inspection?", a: "We cover all visible and accessible interior elements including finishes, MEP systems, balconies, windows, doors and joinery." },
      { q: "Can Fedari inspect older properties in JLT as well?", a: "Yes. While our focus is on upcoming handovers like Verde, we provide snagging and DLP inspections for various property types across Dubai, subject to availability." },
      { q: "How quickly will I receive my JLT inspection report?", a: "You receive your detailed, photo-led report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing." },
      { q: "What documentation does Fedari provide for JLT handovers?", a: "A structured report with photographs and severity labels detailing all findings, to support your communication with the developer." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-sobha-one", "/property-snagging-dubai-communities"],
    title: "JLT Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging in JLT for apartments like Verde by Sobha. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Jumeirah Lake Towers (JLT)",
  },

  "property-snagging-damac-lagoons": {
    communityName: "DAMAC Lagoons",
    developerName: "DAMAC Properties",
    locationLabel: "DAMAC Lagoons",
    emirate: "Dubai",
    projects: ["Ibiza", "Verona"],
    propertyTypes: ["Townhouses", "Villas"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "research-only",
    handoverSummary:
      "DAMAC Lagoons Ibiza and Verona were scheduled for 2026 handover. As announced dates move, we recommend reverifying the current status with the developer before you schedule acceptance.",
    uniqueSalesAngle:
      "We provide comprehensive handover inspections for townhouses and villas across DAMAC Lagoons clusters such as Ibiza and Verona, with specialised attention to larger living spaces.",
    inspectionFocus: [
      "Villa and townhouse exteriors",
      "Roofing and waterproofing",
      "MEP systems",
      "Internal finishes and joinery",
      "Outdoor living spaces",
    ],
    faqs: [
      { q: "When is handover for DAMAC Lagoons Ibiza and Verona?", a: "Ibiza and Verona were scheduled for 2026 handover. As announced dates can move, please verify the current status and exact dates with DAMAC Properties." },
      { q: "What does an inspection for DAMAC Lagoons townhouses and villas cover?", a: "We cover visible and accessible areas including exteriors, roofing, MEP systems, interior finishes, joinery and outdoor spaces." },
      { q: "Can Fedari inspect properties in other DAMAC Lagoons clusters?", a: "Yes. We inspect properties across the various clusters within DAMAC Lagoons, tailoring the scope to townhouses and villas." },
      { q: "How do I arrange an inspection for a property in DAMAC Lagoons?", a: "Request an exact quote and confirmed availability through the quote form. We coordinate the details once access is confirmed." },
      { q: "What is the benefit of inspecting my DAMAC Lagoons property?", a: "An independent inspection identifies and documents snags and visible findings, giving you clear evidence to submit to the responsible party for follow-up." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-dubai-communities"],
    title: "DAMAC Lagoons Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging for DAMAC Lagoons townhouses and villas (Ibiza, Verona). Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in DAMAC Lagoons",
  },

  "dlp-inspection-azizi-riviera": {
    communityName: "Azizi Riviera",
    developerName: "Azizi Developments",
    locationLabel: "Azizi Riviera",
    emirate: "Dubai",
    projects: [],
    propertyTypes: ["Apartments"],
    inspectionIntent: "DLP / warranty inspection",
    handoverStatus: "recent-handover-dlp",
    handoverSummary:
      "Azizi Developments has been handing over Riviera buildings, making it an opportune time for owners to consider a Defect Liability Period (DLP) inspection to document any issues arising after handover.",
    uniqueSalesAngle:
      "We specialise in post-handover DLP inspections for apartments in Azizi Riviera, documenting visible and accessible findings within the applicable period so owners have clear evidence for their follow-up. Coverage and rectification remain subject to the applicable contract.",
    inspectionFocus: [
      "Post-handover emerging defects",
      "Water ingress and leaks",
      "Plumbing and electrical issues",
      "Finishes and joinery issues",
      "Warranty-claim documentation",
    ],
    faqs: [
      { q: "What is a DLP inspection for Azizi Riviera apartments?", a: "A DLP inspection is carried out during the warranty period after handover to identify defects that have appeared. We document these for submission to Azizi Developments." },
      { q: "When should I book a DLP inspection for my Azizi Riviera property?", a: "Book well within your property's applicable period, allowing time for emerging issues to become apparent and for follow-up before the deadline." },
      { q: "How does Fedari's report help with DLP claims?", a: "Our detailed, photo-led reports clearly document each defect, providing strong evidence for your warranty claims with the developer." },
      { q: "What if I'm overseas and need a DLP inspection in Azizi Riviera?", a: "We can arrange DLP inspections for overseas owners once lawful access is confirmed, delivering the report digitally for your review and use." },
      { q: "What is the typical DLP duration for apartments in Azizi Riviera?", a: "The Defect Liability Period varies and is subject to the terms of your Sale and Purchase Agreement and developer contract — please check your documents for specifics." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/dlp-warranty-inspection-dubai", "/property-snagging-al-furjan", "/property-snagging-dubai-communities"],
    title: "Azizi Riviera DLP Inspection | Fedari Inspection",
    metaDescription:
      "Post-handover DLP inspections for Azizi Riviera apartments. Document defects within your warranty period. Report delivery within the confirmed timeframe. Exact quote and availability.",
    h1: "Defect Liability Period (DLP) inspection for Azizi Riviera apartments",
  },

  "property-snagging-arabian-ranches-3": {
    communityName: "Arabian Ranches 3",
    developerName: "Emaar Properties",
    locationLabel: "Arabian Ranches 3",
    emirate: "Dubai",
    projects: ["Anya", "Eliza", "Reem"],
    propertyTypes: ["Townhouses", "Villas"],
    inspectionIntent: "Villa & townhouse snagging",
    handoverStatus: "ongoing",
    handoverSummary:
      "Phases within Arabian Ranches 3 — such as Anya, Eliza and Reem — are at different stages of delivery, with handovers ongoing. Confirm your phase schedule with Emaar before scheduling acceptance.",
    uniqueSalesAngle:
      "We provide expert handover inspections for townhouses and villas in Arabian Ranches 3, focusing on external finishes, roofing and interior build so you receive your Emaar home to the highest standard.",
    inspectionFocus: [
      "Villa and townhouse exteriors",
      "Roofing and insulation (where accessible)",
      "MEP systems",
      "Interior finishes and joinery",
      "Landscaping and outdoor areas",
    ],
    faqs: [
      { q: "When is handover for Arabian Ranches 3?", a: "Phases such as Anya, Eliza and Reem hand over at different times. Please confirm the current schedule for your phase directly with Emaar Properties." },
      { q: "What does an Arabian Ranches 3 villa inspection cover?", a: "We inspect visible, accessible areas including exteriors, roofing, MEP systems, interior finishes, joinery and outdoor spaces." },
      { q: "Do you inspect both townhouses and villas here?", a: "Yes. Our scope adapts to the property type, with additional attention to external areas and roofing for larger villas." },
      { q: "Can I book before I take handover?", a: "Yes — a pre-handover snagging inspection is ideal so visible findings are documented before or around acceptance for timely follow-up." },
      { q: "How soon do I get the report?", a: "You receive a detailed, photo-led report. The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-sobha-reserve", "/property-snagging-damac-lagoons", "/property-snagging-dubai-communities"],
    title: "Arabian Ranches 3 Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging for Arabian Ranches 3 townhouses and villas. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Arabian Ranches 3",
  },

  "property-snagging-town-square-dubai": {
    communityName: "Town Square Dubai",
    developerName: "Nshama",
    locationLabel: "Town Square Dubai",
    emirate: "Dubai",
    projects: ["The Mayfair"],
    propertyTypes: ["Apartments", "Townhouses", "Villas"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "ongoing",
    handoverSummary:
      "Nshama's Town Square Dubai has numerous phases, including The Mayfair, with ongoing and upcoming handovers. Confirm the current schedule for your phase directly with Nshama.",
    uniqueSalesAngle:
      "We offer comprehensive handover inspections for apartments, townhouses and villas across Town Square Dubai, verifying construction and finishing quality throughout this large community.",
    inspectionFocus: [
      "Apartment finishes and fixtures",
      "Townhouse and villa exteriors",
      "MEP systems",
      "Joinery and internal fittings",
      "Balcony and patio areas",
    ],
    faqs: [
      { q: "When is handover for Town Square Dubai?", a: "Numerous phases, including The Mayfair, hand over at different times. Please verify the current schedule for your unit with Nshama." },
      { q: "Do you inspect apartments as well as townhouses in Town Square?", a: "Yes. We inspect apartments, townhouses and villas, adapting the scope to each property type." },
      { q: "What is checked during a Town Square inspection?", a: "We cover finishes, fixtures, MEP systems, joinery, balconies/patios and, for houses, accessible external areas." },
      { q: "Is an independent inspection worthwhile in Town Square?", a: "Yes — an independent inspection documents snags before or shortly after handover so they can be submitted to the responsible party for timely follow-up." },
      { q: "How quickly is the report delivered?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-mudon-al-ranim", "/property-snagging-dubai-communities"],
    title: "Town Square Dubai Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging for Town Square Dubai apartments, townhouses and villas. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Town Square Dubai",
  },

  "property-snagging-dubai-creek-harbour": {
    communityName: "Dubai Creek Harbour",
    developerName: "Emaar Properties",
    locationLabel: "Dubai Creek Harbour",
    emirate: "Dubai",
    projects: ["Creek Gate", "Harbour Views"],
    propertyTypes: ["Apartments", "Townhouses"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "ongoing",
    handoverSummary:
      "Dubai Creek Harbour is a master-planned waterfront development with multiple projects and continuous handovers. Confirm the current status for your project with the developer.",
    uniqueSalesAngle:
      "We provide expert handover inspections for apartments and townhouses in Dubai Creek Harbour, focusing on the premium build quality and finishes typical of this waterfront district.",
    inspectionFocus: [
      "Apartment finishes and fixtures",
      "Townhouse exteriors",
      "MEP systems",
      "Balconies and glazing",
      "Joinery and cabinetry",
    ],
    faqs: [
      { q: "When is handover for Dubai Creek Harbour?", a: "Handovers are continuous across the various projects. Please confirm the schedule for your specific development with the developer." },
      { q: "What is covered in a Dubai Creek Harbour inspection?", a: "We cover visible and accessible finishes, fixtures, MEP systems, balconies, glazing and joinery." },
      { q: "Can overseas owners use your service here?", a: "Yes — once lawful access is arranged we inspect on your behalf and deliver the report digitally." },
      { q: "Why inspect a premium waterfront apartment?", a: "Even premium units can have finishing and sealant snags; an independent inspection ensures they are documented and corrected." },
      { q: "How soon is my report ready?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-dubai-hills-estate", "/property-snagging-dubai-communities"],
    title: "Dubai Creek Harbour Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging in Dubai Creek Harbour for apartments and townhouses. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Dubai Creek Harbour",
  },

  "property-snagging-dubai-hills-estate": {
    communityName: "Dubai Hills Estate",
    developerName: "Emaar Properties",
    locationLabel: "Dubai Hills Estate",
    emirate: "Dubai",
    projects: ["Park Heights", "Collective"],
    propertyTypes: ["Apartments", "Townhouses", "Villas"],
    inspectionIntent: "Handover snagging",
    handoverStatus: "ongoing",
    handoverSummary:
      "Dubai Hills Estate is a large master community with numerous projects and continuous handovers. Confirm the current schedule for your project with the developer.",
    uniqueSalesAngle:
      "We offer comprehensive handover inspections for apartments, townhouses and villas in Dubai Hills Estate, verifying construction, finishes and accessible external areas for a confident acceptance.",
    inspectionFocus: [
      "Apartment finishes and fixtures",
      "Villa and townhouse exteriors",
      "MEP systems",
      "Joinery and cabinetry",
      "Balconies, patios and accessible grounds",
    ],
    faqs: [
      { q: "When is handover for Dubai Hills Estate?", a: "Handovers are continuous across the many projects. Please confirm the schedule for your development with the developer." },
      { q: "Do you inspect villas and apartments in Dubai Hills?", a: "Yes. We inspect apartments, townhouses and villas, adapting the scope to each property type." },
      { q: "What does a Dubai Hills inspection include?", a: "Visible and accessible finishes, fixtures, MEP systems, joinery and — for houses — accessible external areas and roofing." },
      { q: "Should I inspect before signing handover?", a: "Ideally yes, so visible findings are documented before or around acceptance for timely follow-up under the applicable handover, contract or warranty process." },
      { q: "How quickly do I get the report?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-dubai-creek-harbour", "/property-snagging-dubai-communities"],
    title: "Dubai Hills Estate Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging in Dubai Hills Estate for apartments, townhouses and villas. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Dubai Hills Estate",
  },

  "property-snagging-nad-al-sheba-gardens": {
    communityName: "Nad Al Sheba Gardens",
    developerName: "Meraas",
    locationLabel: "Nad Al Sheba Gardens",
    emirate: "Dubai",
    projects: [],
    propertyTypes: ["Villas"],
    inspectionIntent: "Villa handover snagging",
    handoverStatus: "ongoing",
    handoverSummary:
      "Nad Al Sheba Gardens is a villa-led community with ongoing and upcoming handovers across its phases. Confirm the current schedule for your villa with the developer.",
    uniqueSalesAngle:
      "We provide specialised handover inspections for the villas at Nad Al Sheba Gardens, focusing on construction quality, finishes and safe, accessible external features for these family homes.",
    inspectionFocus: [
      "Villa exteriors and accessible roofing",
      "MEP systems",
      "Interior finishes and joinery",
      "Landscaping and patio areas",
      "Garage and service areas",
    ],
    faqs: [
      { q: "When is handover for Nad Al Sheba Gardens?", a: "Handovers are phased. Please confirm the current schedule for your villa with the developer." },
      { q: "What is the scope for a Nad Al Sheba Gardens villa inspection?", a: "We cover safe, accessible external elements such as roofing and facades, plus interior finishes, MEP systems and joinery." },
      { q: "Do you inspect garages and service areas?", a: "Yes, where they are safe and accessible on the inspection day." },
      { q: "Can you inspect for me if I'm overseas?", a: "Yes — once lawful access is arranged we inspect on your behalf and deliver a full digital report." },
      { q: "How soon is the report delivered?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-sobha-reserve", "/property-snagging-dubai-communities"],
    title: "Nad Al Sheba Gardens Villa Snagging | Fedari Inspection",
    metaDescription:
      "Independent villa snagging in Nad Al Sheba Gardens. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Nad Al Sheba Gardens",
  },

  "property-snagging-mudon-al-ranim": {
    communityName: "Mudon Al Ranim",
    developerName: "Dubai Properties",
    locationLabel: "Mudon",
    emirate: "Dubai",
    projects: [],
    propertyTypes: ["Townhouses", "Villas"],
    inspectionIntent: "Villa & townhouse snagging",
    handoverStatus: "ongoing",
    handoverSummary:
      "Mudon Al Ranim by Dubai Properties has phased handovers across its clusters. Confirm the current schedule and readiness for inspection with the developer.",
    uniqueSalesAngle:
      "We offer dedicated handover inspections for townhouses and villas in Mudon Al Ranim, with meticulous checks of interior finishes and safe, accessible external features for a confident acceptance.",
    inspectionFocus: [
      "Townhouse and villa exteriors",
      "Roofing and insulation (where accessible)",
      "MEP systems",
      "Interior finishes and joinery",
      "Balcony and patio areas",
    ],
    faqs: [
      { q: "When is handover for Mudon Al Ranim?", a: "Handovers are phased across the Al Ranim clusters. Please confirm the current schedule with Dubai Properties." },
      { q: "What does a Mudon Al Ranim inspection cover?", a: "Visible and accessible exteriors, MEP systems, interior finishes, joinery and balcony/patio areas." },
      { q: "Do you inspect both townhouses and villas?", a: "Yes. We adapt the scope to the property type, with extra attention to external areas for villas." },
      { q: "Is a pre-handover inspection recommended?", a: "Yes — it documents snags before or shortly after handover so they can be submitted to the responsible party for timely follow-up." },
      { q: "How quickly is the report ready?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/property-snagging-arabian-ranches-3", "/property-snagging-town-square-dubai", "/property-snagging-dubai-communities"],
    title: "Mudon Al Ranim Property Snagging | Fedari Inspection",
    metaDescription:
      "Independent property snagging for Mudon Al Ranim townhouses and villas. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Property snagging and handover inspection in Mudon Al Ranim",
  },

  "communities/dubai-south-snagging-inspection": {
    communityName: "Dubai South",
    developerName: "Emaar South (Dubai South)",
    locationLabel: "Dubai South",
    emirate: "Dubai",
    projects: ["Greenspoint", "Expo Golf Villas"],
    propertyTypes: ["Apartments", "Townhouses", "Villas"],
    inspectionIntent: "Handover snagging and DLP inspections",
    handoverStatus: "ongoing",
    handoverSummary:
      "Dubai South is a large master-planned district near Al Maktoum International Airport and Expo City Dubai, with homes handed over in phases across multiple releases. Owners should confirm the current handover status of their specific unit or cluster directly with the developer.",
    uniqueSalesAngle:
      "We inspect apartments, townhouses and villas across Dubai South — with particular attention to phased handovers where finishes, joinery and wet-area sealing vary between releases, plus safe, accessible external areas for townhouses and villas.",
    inspectionFocus: [
      "Joinery, cabinetry and internal doors with hardware",
      "Wall, ceiling and floor finishes across phased releases",
      "Balconies, terraces and accessible external areas",
      "Wet-area sealing, grout and drainage flow",
      "Air-conditioning operation and airflow",
      "External walls, boundary walls and driveways for villas and townhouses where safely accessible",
    ],
    faqs: [
      { q: "When is handover for properties in Dubai South?", a: "Dubai South homes are handed over in phases across different releases and developers. Please confirm the current schedule for your specific unit directly with your developer." },
      { q: "Do you inspect both apartments and villas in Dubai South?", a: "Yes. We adapt the scope to the property type — apartments focus on interiors and balconies, while townhouses and villas add safely accessible external areas such as roofing and boundary walls where included in the agreed scope." },
      { q: "Can Fedari inspect if I am an overseas owner?", a: "Yes. Once lawful and safe access is arranged we can inspect on your behalf and deliver the report digitally, so overseas owners can manage acceptance remotely." },
      { q: "Is a pre-handover inspection recommended for Dubai South?", a: "Yes — it documents visible findings before or shortly after acceptance, so they can be followed up with the responsible party under the applicable contract and process." },
      { q: "How quickly is the report ready?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
      { q: "Do you offer resale inspections and reinspections in Dubai South?", a: "Yes. A resale inspection documents the visible condition of a secondary-market property before you buy, and a reinspection (from AED 399) visually reviews the apparent status of previously reported items after rectification is reported." },
    ],
    relatedSlugs: ["/property-snagging-dubai", "/handover-inspection-dubai", "/services/overseas-property-inspection-dubai", "/property-snagging-dubai-communities"],
    title: "Dubai South Snagging Inspection | Independent Handover Report",
    metaDescription:
      "Independent snagging and handover inspections in Dubai South — apartments, townhouses and villas. Report delivery within the confirmed timeframe. Exact quote and confirmed availability.",
    h1: "Independent snagging inspections in Dubai South",
  },
};

export const COMMUNITY_SLUGS = Object.keys(COMMUNITY_PAGES);

// Ordered list for the hub grid (grouped feel: apartments, mixed, villas).
export const COMMUNITY_LIST = COMMUNITY_SLUGS.map((slug) => ({
  slug,
  href: `/${slug}`,
  name: COMMUNITY_PAGES[slug].communityName,
  developer: COMMUNITY_PAGES[slug].developerName,
  emirate: COMMUNITY_PAGES[slug].emirate,
  propertyTypes: COMMUNITY_PAGES[slug].propertyTypes,
  intent: COMMUNITY_PAGES[slug].inspectionIntent,
}));

// Resolve a display label for a related-link slug (community or service/hub).
export function relatedLabel(href) {
  if (RELATED_LABELS[href]) return RELATED_LABELS[href];
  const slug = href.replace(/^\//, "");
  return COMMUNITY_PAGES[slug]?.communityName || href;
}


// Date this community information was last reviewed by the Fedari team.
export const COMMUNITY_INFO_REVIEWED = "July 2026";

// Official developer sites — identification-only references (not endorsements).
const DEVELOPER_URLS = [
  ["Azizi", "https://www.azizidevelopments.com"],
  ["Sobha", "https://www.sobharealty.com"],
  ["Meraas", "https://www.meraas.com"],
  ["Emaar", "https://www.emaar.com"],
  ["DAMAC", "https://www.damacproperties.com"],
  ["Nshama", "https://www.nshama.com"],
  ["Dubai Properties", "https://www.dubai-properties.ae"],
];

export function developerUrl(developerName = "") {
  const hit = DEVELOPER_URLS.find(([k]) => developerName.includes(k));
  return hit ? hit[1] : "";
}

const isVilla = (types = []) => types.some((t) => /villa|townhouse/i.test(t));

// Natural list joining: ["A","B","C"] -> "a, b and c" (never "a and b and c",
// never a stray capital).
const joinList = (items) => {
  const parts = items.map((t) => t.toLowerCase());
  return parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
};

// Correct indefinite article for the developer name ("an Emaar…", "a Sobha…").
const articleFor = (name = "") => (/^[aeiou]/i.test(name) ? "an" : "a");

// Evergreen, faithful narrative sections (interpolated per community) so every
// community page carries genuinely useful, self-authored content. No invented
// dates, ratings or developer obligations — time-sensitive facts stay general.
export function communityNarrative(data) {
  const { communityName: n, developerName: dev, emirate, propertyTypes, projects } = data;
  const types = joinList(propertyTypes);
  const villa = isVilla(propertyTypes);
  const projectLine = projects && projects.length
    ? `Within ${n} we regularly inspect ${types} across releases such as ${projects.slice(0, 3).join(", ")}, adapting the scope to each unit.`
    : `Within ${n} we inspect ${types}, adapting the scope to each unit.`;

  return [
    {
      h: `Why an independent inspection in ${n}`,
      paras: [
        `${n} is ${articleFor(dev)} ${dev} community in ${emirate}, and buyers there expect the finish and building services to match the money they have committed. An independent Fedari inspection gives you a neutral, room-by-room record of the condition of your property on the inspection date — separate from the developer's own handover walkthrough. Because we work only for you, our inspectors have no incentive to overlook a snag or to rush the visit.`,
        `${projectLine} Whether you are an end-user moving in, a landlord preparing to lease, or an overseas owner who cannot attend in person, the report gives you clear, photographic evidence of exactly what needs attention before you sign or settle in.`,
      ],
    },
    {
      h: `What a ${n} inspection covers`,
      paras: [
        `Our ${n} inspection is a detailed visual and functional check of the agreed accessible areas of your property. We examine wall, floor and ceiling finishes, joinery and cabinetry, internal doors and windows, sanitary ware, tiling and wet-area sealing, together with the operation of visible plumbing and electrical fixtures and the air-conditioning system.`,
        villa
          ? `For ${types} in ${n} we also assess safe, accessible external elements — facades, roofing where it can be reached safely, terraces, boundary walls and outdoor living areas — because larger homes simply have more surfaces where snags hide.`
          : `For apartments in ${n} we pay particular attention to balconies and glazing, kitchen and bathroom finishes, and the AC and MEP connections that are easy to miss during a quick handover walkthrough.`,
        `Each relevant visible finding is photographed where practicable, given a clear severity rating and paired with a recommended next action, so the report is easy to read and easy to act on.`,
      ],
    },
    {
      h: `Access, timing and handover in ${n}`,
      paras: [
        `Handover timelines in ${n} are set by ${dev} and can move between phases, so we recommend confirming your exact handover appointment with the developer and then booking your inspection to sit just before or around that date. Arranging the inspection early means visible findings are documented in good time for follow-up under the applicable handover, contract or warranty process.`,
        `We only need safe, lawful access to the property on the agreed day. If you are abroad, we can coordinate access with your representative or the building management and deliver everything digitally — you do not need to be present for us to carry out the inspection.`,
      ],
    },
    {
      h: `Your report and next steps`,
      paras: [
        `After the visit you receive a detailed, photo-led report delivered within the confirmed timeframe. It lists each relevant visible finding by area with its severity and a recommended next action, so you can submit it to the responsible party and track the response. Rectification and any warranty coverage remain subject to the applicable contract — we document the evidence; we do not carry out repairs.`,
        `If you arrange a reinspection once works are done, we can verify the items from your original ${n} report so you have a clean record before you finalise acceptance. Request an exact quote and confirmed availability, or message an inspector on WhatsApp, and we will confirm the scope and fixed fee before anything is booked.`,
      ],
    },
  ];
}
